// The scoreboard: every target phrase for this story, ticked off as it's actually
// spoken. The highlighted row is what the coach is drilling right now — this is
// where the "scoped to these vocabularies" promise becomes visible.
//
// `concealed` hides the phrases themselves during a live session. On screen they
// are the answer key: the coach asks you to produce "get your bearings" and the
// words are sitting there to be read aloud, which is recognition practice, not
// recall. Progress stays visible — the count and the bar are the reward, and
// neither gives anything away — and the list is one click behind a Reveal for
// when you are genuinely stuck.

import { useState } from 'react';

// Weakest phrases sort to the top of the drill queue, so the list is shown in
// that order rather than story order — the top of the list is literally what
// gets practised next, this session and the next one.
export default function PhraseChecklist({
  coverage,
  focus,
  onReset,
  resetDisabled,
  concealed = false,
}) {
  const [revealed, setRevealed] = useState(false);
  const hidden = concealed && !revealed;
  const rows = coverage?.phrases || [];
  const queue = coverage?.queue || [];
  const order = new Map(queue.map((p, i) => [p, i]));
  const phrases = queue.length
    ? [...rows].sort(
        (a, b) => (order.get(a.phrase) ?? 1e9) - (order.get(b.phrase) ?? 1e9),
      )
    : rows;
  const total = coverage?.total || 0;
  const practiced = coverage?.practiced || 0;
  const pct = total ? (practiced / total) * 100 : 0;
  const nextUp = queue[0];

  return (
    <div className="flex flex-col rounded-xl border border-slate-800 bg-slate-900/40">
      <div className="border-b border-slate-800 px-4 py-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Target phrases
          </h2>
          <span className="text-xs tabular-nums text-slate-400">
            {practiced}/{total}
          </span>
        </div>
        <p className="mt-0.5 text-[10px] text-slate-600">
          {hidden
            ? 'Hidden while you practise — say them from memory'
            : 'Weakest first — the next session resumes at the top'}
        </p>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-800">
          <div
            className="h-full rounded-full bg-emerald-500 transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {hidden ? (
        <div className="px-4 py-4">
          <button
            type="button"
            onClick={() => setRevealed(true)}
            className="w-full rounded-lg border border-slate-800 py-2 text-[11px] text-slate-500 transition hover:bg-slate-800 hover:text-slate-300"
          >
            Reveal the phrase list
          </button>
          <p className="mt-2 text-center text-[10px] text-slate-600">
            It is the answer key — the coach is asking you to produce these.
          </p>
        </div>
      ) : (
      <div className="thin-scroll max-h-[60vh] overflow-y-auto p-2">
        {phrases.length === 0 && (
          <p className="p-3 text-xs text-slate-500">
            This story has no phrase list — the coach will drill its own wording.
          </p>
        )}

        {phrases.map((p) => {
          const done = p.count > 0;
          const isFocus = focus === p.phrase;
          const isNext = p.phrase === nextUp;
          return (
            <div
              key={p.phrase}
              className={`mb-1 rounded-lg px-3 py-2 transition ${
                isFocus
                  ? 'bg-amber-500/15 ring-1 ring-amber-500/40'
                  : isNext
                    ? 'bg-sky-500/10 ring-1 ring-sky-500/25'
                    : ''
              }`}
            >
              <div className="flex items-start gap-2">
                <span
                  className={`mt-0.5 text-xs ${
                    done ? 'text-emerald-400' : 'text-slate-600'
                  }`}
                >
                  {done ? '✓' : '○'}
                </span>
                <div className="min-w-0 flex-1">
                  <div
                    className={`text-sm ${
                      done ? 'text-emerald-200' : 'text-slate-200'
                    }`}
                  >
                    {p.phrase}
                    {/* Pass history drives the queue order, so show it always —
                        a bare tick hides that 1 clean pass ranks below 3. */}
                    {p.good > 0 && (
                      <span className="ml-1.5 text-[10px] tabular-nums text-emerald-500">
                        {p.good}✓
                      </span>
                    )}
                    {p.shaky > 0 && (
                      <span className="ml-1 text-[10px] tabular-nums text-amber-500">
                        {p.shaky}~
                      </span>
                    )}
                    {isNext && !isFocus && (
                      <span className="ml-1.5 text-[10px] uppercase tracking-wide text-sky-400">
                        next
                      </span>
                    )}
                  </div>
                  {p.meaning && (
                    <div className="text-[11px] leading-snug text-slate-500">
                      {p.meaning}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}

      {total > 0 && !hidden && (
        <button
          type="button"
          onClick={onReset}
          disabled={resetDisabled}
          className="border-t border-slate-800 px-4 py-2 text-[11px] text-slate-500 hover:text-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Reset progress for this story
        </button>
      )}
    </div>
  );
}
